import { useNotifications } from '@/hooks/useNotifications';
import { NotificationItem } from './NotificationItem';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Trash2, Loader2, Bell } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useUser } from '@/contexts/UserContext';

export const NotificationList = () => {
  const notificationHook = useNotifications();
  const { toast } = useToast();
  const { user } = useUser();

  // Add error boundary protection
  if (!notificationHook) {
    return (
      <div className="flex items-center justify-center p-6">
        <div className="text-center text-muted-foreground">
          <Bell className="h-12 w-12 mx-auto mb-2 opacity-50" />
          <p>Error loading notifications</p>
        </div>
      </div>
    );
  }

  const { 
    notifications = [], 
    loading = false, 
    unreadCount = 0, 
    markAllAsRead,
    refresh
  } = notificationHook;


  const handleDeleteAll = async () => {
    console.log('🗑️ Deleting all notifications...');
    console.log('📋 Current notifications before delete:', notifications);
    
    const success = await markAllAsRead();
    
    console.log('✅ Delete result:', success);
    
    if (success) {
      toast({
        title: "✅ Notifications Cleared",
        description: "All notifications have been deleted successfully.",
      });
      
      // Force a small delay and check if notifications are actually cleared
      setTimeout(() => {
        console.log('📋 Notifications after delete (delayed check):', notifications);
      }, 500);
    } else {
      toast({
        title: "Error",
        description: "Failed to delete notifications. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-6">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b">
        <h3 className="font-semibold">Notifications</h3>
        <div className="flex gap-2">
          {notifications.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDeleteAll}
              className="h-8 px-2"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Delete all
            </Button>
          )}
        </div>
      </div>

      {/* Notifications list */}
      {notifications.length === 0 ? (
        <div className="p-6 text-center text-muted-foreground">
          <Bell className="h-12 w-12 mx-auto mb-2 opacity-50" />
          <p>No notifications</p>
        </div>
      ) : (
        <ScrollArea className="max-h-[600px]">
          <div className="divide-y">
            {notifications.map((notification, index) => (
              <div key={notification.id}>
                <NotificationItem notification={notification} />
                {index < notifications.length - 1 && <Separator />}
              </div>
            ))}
          </div>
        </ScrollArea>
      )}
    </div>
  );
};
